import { config } from "@config/config";
import { datadogRum } from "@datadog/browser-rum";
import { useQueryClient } from "@tanstack/react-query";
import { isReviewItemResolved, isSuggestionResolvable } from "@utils/reportVisibility";
import { createContext, use, useEffect, useMemo, useRef, useState } from "react";

import {
  ApiReportSuggestionGenerationStatusChoices,
  GetReportReviewItemsQueryVariables,
  GetSuggestionWorkflowStatusQuery,
  ReportSuggestionSourceEnum,
  ReportTypeEnums,
  ReviewItemFieldsFragment,
  useGetReportReviewItemsQuery,
  useGetSuggestionWorkflowStatusQuery,
  useStartReportSuggestionsWorkflowMutation,
} from "@api/graphql/types-and-hooks";
import { useIsPeerReviewEnabled } from "@components/QaReview/hooks/useIsPeerReviewEnabled";
import { useIsQaScanActive } from "@components/QaReview/hooks/useIsQaScanActive";
import { notifyError } from "@components/notifications/notifications";
import { useReportOptional } from "@providers/ReportProvider";

const { Completed, Failure, InProgress } = ApiReportSuggestionGenerationStatusChoices;

// Other reviewers' changes arrive as a push through the notification bell's socket (reviewCommentsSignal). The poll,
// and a reload on returning to the tab, catch any push that was missed, such as while a laptop slept.
const PEER_REVIEW_POLL_INTERVAL_MS = 15_000;

type ReviewDataValue = {
  items: ReviewItemFieldsFragment[];
  /** The items once the full set has loaded; undefined until then, so the review gate can't be fooled. */
  loadedItems: ReviewItemFieldsFragment[] | undefined;
  /** Unresolved QA suggestions whose anchor renders (the ones that can block completion). */
  openQaItems: ReviewItemFieldsFragment[];
  /** Every QA suggestion whose anchor renders, resolved or not. */
  resolvableQaItems: ReviewItemFieldsFragment[];
  rootComments: ReviewItemFieldsFragment[];
  /** Root comments that are not resolved. */
  openComments: ReviewItemFieldsFragment[];
  repliesByParentId: Map<string, ReviewItemFieldsFragment[]>;
  isQaPending: boolean;
  isQaLoaded: boolean;
  isFetching: boolean;
  refetch: () => void;

  status: ApiReportSuggestionGenerationStatusChoices | null | undefined;
  /** The polled status, falling back to the one stored on the report (null = never scanned). */
  generationStatus: ApiReportSuggestionGenerationStatusChoices | null | undefined;
  isInProgress: boolean;
  isStarting: boolean;
  /** The first status lookup is in flight, so status is not known yet. */
  isStatusLoading: boolean;
  start: (options?: { onError?: (error: unknown) => void }) => Promise<unknown> | undefined;
  rerun: () => Promise<unknown>;
  canRerun: boolean;
  isRefreshing: boolean;
};

export const ReviewDataContext = createContext<ReviewDataValue | null>(null);

const noReviewData: ReviewDataValue = {
  items: [],
  loadedItems: undefined,
  openQaItems: [],
  resolvableQaItems: [],
  rootComments: [],
  openComments: [],
  repliesByParentId: new Map(),
  isQaPending: true,
  isQaLoaded: false,
  isFetching: false,
  refetch: () => {},
  status: undefined,
  generationStatus: undefined,
  isInProgress: false,
  isStarting: false,
  isStatusLoading: false,
  start: () => undefined,
  rerun: () => Promise.resolve(),
  canRerun: false,
  isRefreshing: false,
};

export const useReviewData = (): ReviewDataValue => use(ReviewDataContext) ?? noReviewData;

export const useCreateReviewData = (): ReviewDataValue => {
  const report = useReportOptional()?.report;
  const queryClient = useQueryClient();
  const isQaScanActive = useIsQaScanActive();
  const isPeerReviewEnabled = useIsPeerReviewEnabled();
  const rerunRequestedRef = useRef(false);
  // From the rerun click until its new items arrive, so the rerun icon keeps spinning through the final refetch.
  const [isAwaitingRerun, setIsAwaitingRerun] = useState(false);

  const workflowQueryKey = ["get-suggestion-workflow-status", report?.id];
  const statusQuery = useGetSuggestionWorkflowStatusQuery(
    { reportId: report?.id ?? "", filter: { reportType: report?.reportType as unknown as ReportTypeEnums } },
    {
      enabled: !!report && isQaScanActive,
      queryKey: workflowQueryKey,
      retry: false,
      refetchInterval: (query) => {
        const generationStatus = query.state.data?.getReports?.edges?.[0]?.node?.suggestionGenerationStatus;
        return generationStatus === Completed || generationStatus === Failure ? false : config.REPORT_POLLING_INTERVAL;
      },
    },
  );
  const startMutation = useStartReportSuggestionsWorkflowMutation({});
  const status = statusQuery.data?.getReports?.edges?.[0]?.node?.suggestionGenerationStatus;
  const isInProgress = status === InProgress;
  const isStarting = startMutation.isPending;

  const start: ReviewDataValue["start"] = ({ onError } = {}) =>
    report &&
    startMutation.mutateAsync(
      { reportId: report.id },
      {
        onError,
        onSuccess: () => {
          queryClient.setQueryData<GetSuggestionWorkflowStatusQuery>(workflowQueryKey, (previous) => ({
            ...previous,
            getReports: {
              ...previous?.getReports,
              edges: [
                {
                  ...previous?.getReports?.edges?.[0],
                  node: {
                    ...previous?.getReports?.edges?.[0]?.node,
                    id: report.id,
                    suggestionGenerationStatus: InProgress,
                  },
                },
              ],
            },
          }));
          setTimeout(() => statusQuery.refetch(), config.REPORT_POLLING_INTERVAL);
        },
      },
    );

  const loadsQa = !!report && isQaScanActive && !isStarting && status === Completed;
  const loadsPeer = !!report && isQaScanActive && isPeerReviewEnabled;
  const source = loadsQa ? ReportSuggestionSourceEnum.QaScan : ReportSuggestionSourceEnum.PeerReview;

  const itemsQuery = useGetReportReviewItemsQuery(
    { reportId: report?.id ?? "", filter: loadsQa && loadsPeer ? undefined : { source } },
    {
      enabled: loadsQa || loadsPeer,
      refetchInterval: loadsPeer ? PEER_REVIEW_POLL_INTERVAL_MS : false,
      refetchOnWindowFocus: loadsPeer,
      // The source filter changes when the scan completes or reruns, which is a new query. Keep the same
      // report's Team Review comments showing until it answers, so their cards don't drop out and back;
      // QA flags aren't carried over, and placeholder data never counts as loaded (see isQaLoaded).
      placeholderData: (previous, previousQuery) =>
        previous &&
        (previousQuery?.queryKey[1] as GetReportReviewItemsQueryVariables | undefined)?.reportId === report?.id
          ? {
              ...previous,
              getReportReviewItems: previous.getReportReviewItems?.filter(
                (item) => item?.__typename === "ReportCommentType",
              ),
            }
          : undefined,
    },
  );

  useEffect(() => {
    if (itemsQuery.error) {
      datadogRum.addError(itemsQuery.error, { context: "getReportReviewItems failed", reportId: report?.id });
    }
  }, [itemsQuery.error, report?.id]);

  const { refetch, isFetching } = itemsQuery;
  useEffect(() => {
    if (rerunRequestedRef.current && status === Completed && !isFetching) {
      void refetch().finally(() => setIsAwaitingRerun(false));
      rerunRequestedRef.current = false;
    } else if (rerunRequestedRef.current && status === Failure) {
      // A rerun whose run fails never completes, so the wait ends here.
      setIsAwaitingRerun(false);
      rerunRequestedRef.current = false;
    }
  }, [status, isFetching, refetch]);

  const rerun = async () => {
    setIsAwaitingRerun(true);
    let started = true;
    await start({
      onError: (error) => {
        console.error("Error rerunning report suggestions workflow: ", error);
        notifyError("Failed to rerun report suggestions workflow");
        started = false;
        setIsAwaitingRerun(false);
      },
    });
    // Only once the run has started: until then the status still reads Completed from the last run, and a
    // poll tick finishing in that gap would take the old items as the rerun's.
    if (started) rerunRequestedRef.current = true;
  };

  const cachedItems = itemsQuery.data?.getReportReviewItems;
  const items = useMemo(
    () => (isQaScanActive ? (cachedItems ?? []).filter((item): item is ReviewItemFieldsFragment => item != null) : []),
    [cachedItems, isQaScanActive],
  );

  const collections = useMemo(() => {
    const resolvableQaItems = items.filter(
      (item) => item.__typename === "ReportSuggestionType" && isSuggestionResolvable(item),
    );
    const comments = items.filter((item) => item.__typename === "ReportCommentType");
    const rootComments = comments.filter((item) => !item.parent);
    const repliesByParentId = new Map<string, ReviewItemFieldsFragment[]>();
    const oldestFirst = [...comments].sort((a, b) => Date.parse(a.createdDate) - Date.parse(b.createdDate));
    oldestFirst.forEach((comment) => {
      if (comment.parent) {
        repliesByParentId.set(comment.parent.id, [...(repliesByParentId.get(comment.parent.id) ?? []), comment]);
      }
    });
    return {
      resolvableQaItems,
      openQaItems: resolvableQaItems.filter((item) => !isReviewItemResolved(item)),
      rootComments,
      openComments: rootComments.filter((item) => !isReviewItemResolved(item)),
      repliesByParentId,
    };
  }, [items]);

  const isQaLoaded = loadsQa && itemsQuery.isSuccess && !itemsQuery.isPlaceholderData;

  return {
    items,
    loadedItems: isQaLoaded ? items : undefined,
    ...collections,
    isQaPending: !loadsQa || itemsQuery.isPending || itemsQuery.isPlaceholderData,
    isQaLoaded,
    isFetching,
    refetch,
    status,
    generationStatus: status || report?.suggestionGenerationStatus,
    isInProgress,
    isStarting,
    isStatusLoading: statusQuery.isLoading,
    start,
    rerun,
    canRerun: (status === Failure || status === Completed) && !isStarting && !isInProgress,
    // Not every fetch: the peer-review poll would spin the rerun icon every 15 seconds. Placeholder data is a
    // new source still loading, which isLoading misses because the placeholder counts as success.
    isRefreshing:
      isStarting ||
      isInProgress ||
      itemsQuery.isLoading ||
      itemsQuery.isPlaceholderData ||
      (isAwaitingRerun && status !== Failure),
  };
};
